import type { InterviewSectionType } from "@repo/app-types/entities/interviewSection/types";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { ModalCloseButton } from "@repo/ui/Modal/modalCloseButton";
import { modalHeader } from "@repo/ui/Modal/modalHeader";
import { IconCircleCheck } from "@tabler/icons-react";
import type { ReactNode } from "react";
import {
  Dialog,
  GridList,
  GridListItem,
  Header,
  Heading,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { getInterviewSectionTypeDisplayValues } from "../../types.js";

export interface InterviewSectionPickTypeDialogProps<
  T extends InterviewSectionType,
> {
  title: ReactNode;
  description: ReactNode;
  options: readonly InterviewSectionPickTypeOption<T>[];
  onSelect: (type: T) => void;
}

export interface InterviewSectionPickTypeOption<
  T extends InterviewSectionType,
> {
  type: T;
  disabledReason?: ReactNode;
}

export const InterviewSectionPickTypeDialog = <T extends InterviewSectionType>(
  props: InterviewSectionPickTypeDialogProps<T>,
) => {
  const isAllDisabled = props.options.every((option) =>
    Boolean(option.disabledReason),
  );

  return (
    <Dialog>
      {({ close }) => (
        <>
          <Header
            className={modalHeader().header({
              className: "px-4 pt-4 sm:px-6 sm:pt-6",
            })}
          >
            <Heading slot="title" className={modalHeader().title()}>
              {props.title}
            </Heading>
            <ModalCloseButton />
          </Header>

          <div className="flex flex-col space-y-4 p-4 sm:space-y-6 sm:p-6">
            {props.description ? (
              <div className="text-sm text-foreground">{props.description}</div>
            ) : null}

            {isAllDisabled ? (
              <InfoBanner title="You've already added all types of interview sections." />
            ) : null}

            <GridList
              className="grid max-w-4xl grid-cols-2 gap-4 md:grid-cols-3"
              aria-label="Section type"
              layout="grid"
            >
              {props.options.map((option) => {
                const { Icon, iconColor, title, description, skills } =
                  getInterviewSectionTypeDisplayValues(option.type);

                return (
                  <GridListItem
                    key={option.type}
                    textValue={option.type}
                    className="group relative flex cursor-pointer justify-between rounded-lg border border-border-strong bg-white p-4 text-foreground shadow-xs hover:bg-gray-50 focus:outline-hidden focus-visible:border-indigo-600 focus-visible:ring-2 focus-visible:ring-indigo-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:shadow-none"
                    isDisabled={!!option.disabledReason}
                    onAction={() => {
                      props.onSelect(option.type);
                      close();
                    }}
                  >
                    <div className="flex flex-col justify-between">
                      <div className="flex flex-col items-start gap-y-1">
                        {option.disabledReason ? (
                          <span className="inline-flex items-center rounded-md bg-purple-100 px-1.5 py-0.5 text-xs font-medium text-purple-600">
                            {option.disabledReason}
                          </span>
                        ) : null}

                        <div
                          className={twMerge(
                            "my-1 flex items-center justify-center rounded-lg p-2",
                            iconColor,
                          )}
                        >
                          <Icon className="size-6 shrink-0 text-white" />
                        </div>

                        <span className="text-sm font-semibold">{title}</span>
                        <span className="text-xs text-muted-foreground">
                          {description}
                        </span>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-1 justify-self-end group-disabled:opacity-70 sm:mt-6">
                        {skills.map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                    <IconCircleCheck className="invisible size-5 flex-none text-indigo-600 group-data-[selected]:visible" />
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-focus-visible:border group-data-[selected]:border-indigo-600"
                    />
                  </GridListItem>
                );
              })}
            </GridList>
          </div>
        </>
      )}
    </Dialog>
  );
};
